import React, { ReactNode, memo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

// Core providers
import { ThemeProvider } from '@/contexts/ThemeContext';
import { LocaleProvider } from '@/contexts/LocaleContext';
import { AuthProvider } from '@/contexts/auth';
import { EncryptionProvider } from '@/contexts/encryption';
import { PreloadProvider } from '@/contexts/PreloadContext';
import { RoleProvider } from '@/contexts/RoleContext';

// User & subscription providers
import { SubscriptionProvider } from '@/contexts/subscription';
import { ProfileProvider } from '@/contexts/profile';
import { TimezoneProvider } from '@/contexts/TimezoneContext';

// UI providers
import { GlobalFiltersProvider } from '@/contexts/global-filters';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { PopoverStateProvider } from '@/components/ui/popover-state-provider';
import { TouchProvider } from '@/contexts/TouchContext';

// Data providers
import { TransactionsProvider } from '@/contexts/transactions';
import { AccountsProvider } from '@/contexts/accounts';
import { CategoriesProvider } from '@/contexts/categories';
import { CardsProvider } from '@/contexts/cards';
import { CreditCardBillsProvider } from '@/contexts/CreditCardBillsContext';
import { FutureReleasesProvider } from '@/contexts/future-releases';
import { BudgetsProvider } from '@/contexts/budgets';
import { GoalsProvider } from '@/contexts/goals';
import { UnifiedInstallmentsProvider } from '@/contexts/unified-installments';
import { DebtsProvider } from '@/contexts/debts';

// Toaster
import { Toaster } from '@/components/ui/sonner';

interface ProviderProps {
  children: ReactNode;
}

// Optimized Query client configuration
// Optimized Query client configuration for streaming data
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
      // Reduce network waterfall by fetching in background
      networkMode: 'offlineFirst',
    },
  },
});

/**
 * Core providers that rarely change (theme, locale, auth, encryption)
 */
const CoreProviders = memo(({ children }: ProviderProps) => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <LocaleProvider>
        <AuthProvider>
          <EncryptionProvider>
            <PreloadProvider>
              {children}
            </PreloadProvider>
          </EncryptionProvider>
        </AuthProvider>
      </LocaleProvider>
    </ThemeProvider>
  </QueryClientProvider>
));
CoreProviders.displayName = 'CoreProviders';

/**
 * User-related providers (role, subscription, profile, timezone)
 */
const UserProviders = memo(({ children }: ProviderProps) => (
  <RoleProvider>
    <SubscriptionProvider>
      <ProfileProvider>
        <TimezoneProvider>
          {children}
        </TimezoneProvider>
      </ProfileProvider>
    </SubscriptionProvider>
  </RoleProvider>
));
UserProviders.displayName = 'UserProviders';

/**
 * UI state providers (filters, sidebar, popover, touch)
 */
const UIProviders = memo(({ children }: ProviderProps) => (
  <GlobalFiltersProvider>
    <SidebarProvider>
      <PopoverStateProvider>
        <TouchProvider>
          {children}
        </TouchProvider>
      </PopoverStateProvider>
    </SidebarProvider>
  </GlobalFiltersProvider>
));
UIProviders.displayName = 'UIProviders';

/**
 * Master data providers (accounts, categories, cards)
 */
const MasterDataProviders = memo(({ children }: ProviderProps) => (
  <AccountsProvider>
    <CategoriesProvider>
      <CardsProvider>
        {children}
      </CardsProvider>
    </CategoriesProvider>
  </AccountsProvider>
));
MasterDataProviders.displayName = 'MasterDataProviders';

/**
 * Transactional data providers (transactions, releases, budgets, goals, debts)
 */
const TransactionalDataProviders = memo(({ children }: ProviderProps) => (
  <TransactionsProvider>
    <CreditCardBillsProvider>
      <FutureReleasesProvider>
        <BudgetsProvider>
          <GoalsProvider>
            <UnifiedInstallmentsProvider>
              <DebtsProvider>
                {children}
              </DebtsProvider>
            </UnifiedInstallmentsProvider>
          </GoalsProvider>
        </BudgetsProvider>
      </FutureReleasesProvider>
    </CreditCardBillsProvider>
  </TransactionsProvider>
));
TransactionalDataProviders.displayName = 'TransactionalDataProviders';

/**
 * Main AppProviders component that composes all providers in the correct order.
 * 
 * Provider hierarchy:
 * 1. CoreProviders - Query, Theme, Locale, Auth, Encryption, Preload
 * 2. UserProviders - Role, Subscription, Profile, Timezone
 * 3. UIProviders - GlobalFilters, Sidebar, Popover, Touch
 * 4. MasterDataProviders - Accounts, Categories, Cards, CreditCardBills
 * 5. TransactionalDataProviders - Transactions, Releases, Budgets, Goals, Installments, Debts
 * 6. BrowserRouter - Must wrap route-aware components
 */
export const AppProviders = memo(({ children }: ProviderProps) => (
  <CoreProviders>
    <UserProviders>
      <UIProviders>
        <MasterDataProviders>
          <TransactionalDataProviders>
            <BrowserRouter>
              {children}
            </BrowserRouter>
            <Toaster />
          </TransactionalDataProviders>
        </MasterDataProviders>
      </UIProviders>
    </UserProviders>
  </CoreProviders>
));
AppProviders.displayName = 'AppProviders';

// Export individual provider groups for testing or partial usage
export {
  CoreProviders,
  UserProviders,
  UIProviders,
  MasterDataProviders,
  TransactionalDataProviders,
};
